// node_modules
import { FC, useContext, useRef, useState } from "react";
// Enums
import { GroupTermElementTypeEnum, SearchPriorityEnum } from "Enums";
// Types
import { TEnvironmentVariableDTO, TSavedQueryTempDTO } from "Types";
// styles
import styles from "./environmentVariables.module.scss";
// Components
import { FindestRefTextBox } from "Components";
import { EnvironmentVariable } from "./EnvironmentVariable";
// Controllers
import { EnvironmentControllerSingleton } from "Controllers/EnvironmentController";
// Contexts
import { SavedQueryContext, UserContext } from "Providers";
import { CaseContext } from "Stores";
// Custom hooks
import { useKeywords } from "Hooks";
import { SearchTermGroupSearch } from "../SearchTermGroupSearch";
import { FeatureToggleConstants } from "Constants";

export const EnvironmentVariables: FC = () => {
    // Ref
    const newKeywordInputRef = useRef<HTMLInputElement>(null);

    // Contexts
    const { savedQuery, environmentVariables, updateSavedQuery, duplicateSavedQuery } = useContext(SavedQueryContext);
    const { caseState } = useContext(CaseContext);
    const { isCurrentUserFindest } = useContext(UserContext);

    // Custom hooks
    const [addKeyword, getDuplicatedKeywordFromValueInSavedQuery] = useKeywords(
        caseState.selectedCaseId, caseState.selectedOriginalTechnologyId, savedQuery,
        updateSavedQuery, duplicateSavedQuery
    );

    // State
    const [searchTermSearchQuery, setSearchTermSearchQuery] = useState<{query: string } | undefined>(undefined);
    
    // Determine if the user can use the search term groups
    const isSearchTermGroupsEnabled = FeatureToggleConstants.SynonymGroups && isCurrentUserFindest;

    // Logic
    const getEnvironmentVariableByIdInList = (id: number, environmentVariableList: TEnvironmentVariableDTO[]): TEnvironmentVariableDTO | undefined => {
        // safety-checks
        if (id === null || id === undefined || !environmentVariableList) { return undefined; }

        for (const environmentVariable of environmentVariableList) {
            if (environmentVariable.id === id) { return environmentVariable; }
        }

        return undefined;
    };
    
    const addNewKeyword = async (_value?: string, searchTermGroupId?: string): Promise<void> => {
        // empty add new keyword input
        if (!newKeywordInputRef.current) { return; }

        await addKeyword(newKeywordInputRef.current.value, searchTermGroupId);

        newKeywordInputRef.current.value = "";
        setSearchTermSearchQuery(undefined);
    };

    const changeEnvironmentSearchPriority = async (id: number, newPriority: SearchPriorityEnum): Promise<void> => {
        // safety-checks
        if (!savedQuery) { return; }

        const foundEnvironment: TEnvironmentVariableDTO | undefined = getEnvironmentVariableByIdInList(id, environmentVariables);
        // safety-checks
        if (!foundEnvironment) { return; }

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            foundEnvironment.searchPriority = newPriority;
            const isSuccess = await EnvironmentControllerSingleton.updateAsync(foundEnvironment, true);
            // update environment variable list in the saved query context
            if (isSuccess) {
                for (const environment of environmentVariables) {
                    if (environment.id === id) {
                        environment.searchPriority = newPriority;
                    }
                }

                savedQuery.environmentVariables = [...environmentVariables];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundEnvironment || !duplicatedSavedQuery) { return; }

                const duplicatedEnvironmentVariable: TEnvironmentVariableDTO | undefined =
                    getDuplicatedKeywordFromValueInSavedQuery(foundEnvironment.environment, duplicatedSavedQuery);
                
                // safety-checks
                if (!duplicatedEnvironmentVariable) { return; }
                
                duplicatedEnvironmentVariable.searchPriority = newPriority;
                const isSuccess = await EnvironmentControllerSingleton.updateAsync(duplicatedEnvironmentVariable, true);

                // update environment variable list in the saved query context
                if (isSuccess) {
                    for (const environmentVar of duplicatedSavedQuery.environmentVariables) {
                        if (environmentVar.id === duplicatedEnvironmentVariable.id) {
                            environmentVar.searchPriority = duplicatedEnvironmentVariable.searchPriority;
                        }
                    }
                    
                    duplicatedSavedQuery.environmentVariables = [...duplicatedSavedQuery.environmentVariables];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const deleteEnvironment = async (id: number): Promise<void> => {
        // safety-checks
        if (!savedQuery) { return; }

        // filter environment variable list context by removing the environment variable
        const newEnvironments: TEnvironmentVariableDTO[] = [];
        const foundEnvironment: TEnvironmentVariableDTO | undefined = getEnvironmentVariableByIdInList(id, environmentVariables);
        // safety-checks
        if (!foundEnvironment) { return; }
        
        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const isSuccess = await EnvironmentControllerSingleton.deleteAsync(id, true);
            // update environment variable list in the saved query context
            if (isSuccess) {
                for (const environment of environmentVariables) {
                    if (environment.id !== id) {
                        newEnvironments.push(environment);
                    }
                }

                savedQuery.environmentVariables = [...newEnvironments];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundEnvironment || !duplicatedSavedQuery) { return; }

                const duplicatedEnvironmentVariable: TEnvironmentVariableDTO | undefined =
                    getDuplicatedKeywordFromValueInSavedQuery(foundEnvironment.environment, duplicatedSavedQuery);

                // safety-checks
                if (!duplicatedEnvironmentVariable) { return; }

                for (const environmentVar of duplicatedSavedQuery.environmentVariables) {
                    if (environmentVar.id !== duplicatedEnvironmentVariable.id) {
                        newEnvironments.push(environmentVar);
                    }
                }

                const isSuccess = await EnvironmentControllerSingleton.deleteAsync(duplicatedEnvironmentVariable.id, true);
                // update environment variable list in the saved query context
                if (isSuccess) {
                    duplicatedSavedQuery.environmentVariables = [...newEnvironments];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const editEnvironment = async (id: number, text: string): Promise<void> => {
        // safety-checks
        if (!savedQuery) { return; }

        const foundEnvironment: TEnvironmentVariableDTO | undefined = getEnvironmentVariableByIdInList(id, environmentVariables);
        // safety-checks
        if (!foundEnvironment) { return; }
        
        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            foundEnvironment.environment = text;
            const isSuccess = await EnvironmentControllerSingleton.updateAsync(foundEnvironment, true);
            // update environment variable list in the saved query context
            if (isSuccess) {
                for (const environment of environmentVariables) {
                    if (environment.id === id) {
                        environment.environment = text;
                    }
                }

                savedQuery.environmentVariables = [...environmentVariables];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundEnvironment || !duplicatedSavedQuery) { return; }

                const duplicatedEnvironmentVariable: TEnvironmentVariableDTO | undefined =
                    getDuplicatedKeywordFromValueInSavedQuery(foundEnvironment.environment, duplicatedSavedQuery);

                // safety-checks
                if (!duplicatedEnvironmentVariable) { return; }

                duplicatedEnvironmentVariable.environment = text;
                const isSuccess = await EnvironmentControllerSingleton.updateAsync(duplicatedEnvironmentVariable, true);

                // update environment variable list in the saved query context
                if (isSuccess) {
                    for (const environmentVar of duplicatedSavedQuery.environmentVariables) {
                        if (environmentVar.id === duplicatedEnvironmentVariable.id) {
                            environmentVar.environment = text;
                        }
                    }

                    duplicatedSavedQuery.environmentVariables = [...duplicatedSavedQuery.environmentVariables];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    // Search term group logic
    const onNewEnvironmentChange = (text: string): void => {
        setSearchTermSearchQuery({ query: text });
    };

    const onClickSearchTerm = (text: string): void => {
        if(!newKeywordInputRef.current) return;
        newKeywordInputRef.current.value = text;
        addNewKeyword();
    };

    const onGroupClick = (id: string): void => {
        addNewKeyword(undefined, id);
    };

    const onFocus = () => {
        if(!newKeywordInputRef.current) return;
        setSearchTermSearchQuery({ query: newKeywordInputRef.current.value });
    };

    return (
        <div className={styles.userFlowDataList}>
            <h5 className={styles.dataListTitle}>Keywords</h5>
            <FindestRefTextBox ref={newKeywordInputRef} width={296} isBorderless={true} placeholder={"Keyword <Enter>"} 
                onEnter={addNewKeyword} onChange={onNewEnvironmentChange} onClick={onFocus} />
            { isSearchTermGroupsEnabled && searchTermSearchQuery ? 
                <SearchTermGroupSearch query={searchTermSearchQuery} elementType={GroupTermElementTypeEnum.Environment.toString()} 
                    onSingleItemClick={onClickSearchTerm} onGroupClick={onGroupClick}/> 
                : null
            }
            {environmentVariables.map((environment: TEnvironmentVariableDTO) => {
                return <EnvironmentVariable
                    key={environment.id}
                    environment={environment}
                    handleDeleteEnvironment={deleteEnvironment}
                    handleEditEnvironment={editEnvironment}
                    handleChangeEnvironmentSearchPriority={changeEnvironmentSearchPriority}
                />;
            })}
        </div>
    );
};
